import { Navigate, useParams, type RouteObject } from 'react-router';
import type { ComponentType } from 'react';
import { Layout } from '@/components/layout/Layout';
import { ProtectedRoute } from '@/routes/ProtectedRoute';
/** Auth pages are eager — avoids blank flash while chunks load between login/register. */
import LoginPage from '@/pages/public/auth/LoginPage';
import RegisterPage from '@/pages/public/auth/RegisterPage';
import AffiliateRegisterPage from '@/pages/public/auth/AffiliateRegisterPage';

function GuidesIndexRedirect() {
  return <Navigate to="/guides" replace />;
}

function GuidesSlugRedirect() {
  const { slug } = useParams();
  return <Navigate to={slug ? `/guides/${slug}` : '/guides'} replace />;
}

/**
 * ONE route definition, used by BOTH the browser router and the prerenderer.
 *
 * If the two ever drift, prerendered HTML stops matching what the browser
 * renders at the same URL — Google sees one page, the user sees another, which
 * is the cloaking accusation nobody wants to defend. Sharing this array makes
 * that impossible.
 *
 * The browser router is built from `routes` in App.tsx (browser-only), NOT
 * here: `createBrowserRouter` touches `document`, so running it at module load
 * would crash the SSR prerenderer the moment it imports `routes`.
 *
 * `lazy` is fine on both sides: React Router's createStaticHandler resolves it
 * during the static query, exactly as the browser router does.
 */
const lazyRoute = (importer: () => Promise<{ default: ComponentType }>): RouteObject['lazy'] => {
  return async () => {
    const Component = (await importer()).default;
    return { Component };
  };
};

const protectedLazyRoute = (importer: () => Promise<{ default: ComponentType }>): RouteObject['lazy'] => {
  return async () => {
    const Component = (await importer()).default;
    return {
      Component: () => (
        <ProtectedRoute>
          <Component />
        </ProtectedRoute>
      ),
    };
  };
};

const HomePage = lazyRoute(() => import('@/pages/public/home/Home'));
const CatalogPage = lazyRoute(() => import('@/pages/public/catalog/Catalog'));
const CourseDetailPage = lazyRoute(() => import('@/pages/public/catalog/course-detail/CourseDetail'));
const StorePage = lazyRoute(() => import('@/pages/public/commerce/Store'));
const LeaderboardPage = lazyRoute(() => import('@/pages/student/learning/Leaderboard'));
const AffiliatePage = lazyRoute(() => import('@/pages/public/marketing/Affiliate'));
const CentresPage = lazyRoute(() => import('@/pages/public/marketing/Centres'));
const BlogPage = lazyRoute(() => import('@/pages/public/blog/Blog'));
const BlogPostPage = lazyRoute(() => import('@/pages/public/blog/BlogPost'));
const ResourcesPage = lazyRoute(() => import('@/pages/public/resources/Resources'));
const InstructorsPage = lazyRoute(() => import('@/pages/public/catalog/Instructors'));
const InstructorProfilePage = lazyRoute(() => import('@/pages/public/catalog/InstructorProfile'));
const CategoryPage = lazyRoute(() => import('@/pages/public/catalog/Category'));
const EventPage = lazyRoute(() => import('@/pages/public/marketing/Event'));
const BecomeInstructorPage = lazyRoute(() => import('@/pages/public/marketing/BecomeInstructor'));
const ForBusinessPage = lazyRoute(() => import('@/pages/public/marketing/ForBusiness'));
const AboutUsPage = lazyRoute(() => import('@/pages/public/marketing/about-us/AboutUs'));
const CareersPage = lazyRoute(() => import('@/pages/public/marketing/careers/Careers'));
const JobDetailPage = lazyRoute(() => import('@/pages/public/marketing/careers/JobDetail'));
const ContactPage = lazyRoute(() => import('@/pages/public/marketing/Contact'));
const HelpCenterPage = lazyRoute(() => import('@/pages/public/marketing/HelpCenter'));
const PrivacyPolicyPage = lazyRoute(() => import('@/pages/public/marketing/PrivacyPolicy'));
const RefundPolicyPage = lazyRoute(() => import('@/pages/public/marketing/RefundPolicy'));
const TermsOfServicePage = lazyRoute(() => import('@/pages/public/marketing/TermsOfService'));
const CvBuilderPage = lazyRoute(() => import('@/pages/public/resources/CvBuilder'));
const VerifyPage = lazyRoute(() => import('@/pages/public/certificates/Verify'));
const PlayerPage = protectedLazyRoute(() => import('@/pages/student/learning/Player'));
const StudentLayout = protectedLazyRoute(() => import('@/pages/student/dashboard/StudentLayout'));
const DashboardIndex = lazyRoute(() => import('@/pages/student/dashboard/DashboardIndex'));
const StudentOverviewPage = lazyRoute(() => import('@/pages/student/dashboard/overview/OverviewPage'));
const StudentPurchasedPage = lazyRoute(() => import('@/pages/student/dashboard/purchased/PurchasedPage'));
const StudentFreeCoursesPage = lazyRoute(() => import('@/pages/student/dashboard/free/FreeCoursesPage'));
const StudentCompletedPage = lazyRoute(() => import('@/pages/student/dashboard/completed/CompletedPage'));
const StudentCertificatesPage = lazyRoute(() => import('@/pages/student/dashboard/certs/CertificatesPage'));
const StudentWishlistPage = lazyRoute(() => import('@/pages/student/dashboard/wishlist/WishlistPage'));
const StudentCartPage = lazyRoute(() => import('@/pages/student/dashboard/cart/CartPage'));
const StudentPracticePage = lazyRoute(() => import('@/pages/student/dashboard/practice/PracticePage'));
const StudentAssignmentsPage = lazyRoute(() => import('@/pages/student/dashboard/assignments/AssignmentsPage'));
const StudentOrdersPage = lazyRoute(() => import('@/pages/student/dashboard/orders/OrdersPage'));
const StudentSupportPage = lazyRoute(() => import('@/pages/student/dashboard/support/SupportPage'));
const StudentSettingsPage = lazyRoute(() => import('@/pages/student/dashboard/settings/SettingsPage'));
const AdminLayout = protectedLazyRoute(() => import('@/pages/admin/layout/AdminLayout'));
const AdminIndex = lazyRoute(() => import('@/pages/admin/layout/AdminIndex'));
const AdminOverviewPage = lazyRoute(() => import('@/pages/staff/overview/OverviewPage'));
const AdminReportsPage = lazyRoute(() => import('@/pages/staff/reports/ReportsPage'));
const AdminCoursesPage = lazyRoute(() => import('@/pages/staff/courses/CoursesPage'));
const AdminAddCoursePage = lazyRoute(() => import('@/pages/staff/courses/add-course/AddCoursePage'));
const AdminBundlesPage = lazyRoute(() => import('@/pages/admin/bundles/BundlesPage'));
const AdminQbankPage = lazyRoute(() => import('@/pages/staff/qbank/QbankPage'));
const AdminStudentsPage = lazyRoute(() => import('@/pages/admin/students/StudentsPage'));
const AdminOrderManagersPage = lazyRoute(() => import('@/pages/admin/order-managers/OrderManagersPage'));
const AdminOrdersPage = lazyRoute(() => import('@/pages/staff/orders/OrdersPage'));
const AdminBooksPage = lazyRoute(() => import('@/pages/admin/books/BooksPage'));
const AdminBookOrdersPage = lazyRoute(() => import('@/pages/staff/book-orders/BookOrdersPage'));
const AdminPayoutsPage = lazyRoute(() => import('@/pages/admin/payouts/PayoutsPage'));
const AdminAffiliatePayoutsPage = lazyRoute(() => import('@/pages/admin/affiliate-payouts/AffiliatePayoutsPage'));
const AdminPaymentPage = lazyRoute(() => import('@/pages/staff/payment/PaymentPage'));
const AdminCouponsPage = lazyRoute(() => import('@/pages/admin/coupons/CouponsPage'));
const AdminAssignmentsPage = lazyRoute(() => import('@/pages/staff/assignments/AssignmentsPage'));
const AdminAddAssignmentPage = lazyRoute(() => import('@/pages/staff/assignments/add-assignment/AddAssignmentPage'));
const AdminContentPage = lazyRoute(() => import('@/pages/admin/content/ContentPage'));
const AdminDownloadsPage = lazyRoute(() => import('@/pages/admin/downloads/DownloadsPage'));
const AdminQaPage = lazyRoute(() => import('@/pages/admin/qa/QaPage'));
const AdminTicketsPage = lazyRoute(() => import('@/pages/admin/tickets/TicketsPage'));
const AdminReviewsPage = lazyRoute(() => import('@/pages/staff/reviews/ReviewsPage'));
const AdminInstructorsPage = lazyRoute(() => import('@/pages/admin/instructors/InstructorsPage'));
const AdminLeadsPage = lazyRoute(() => import('@/pages/admin/leads/LeadsPage'));
const AdminFreeResourcesPage = lazyRoute(() => import('@/pages/admin/free-resources/FreeResourcesPage'));
const AdminCounsellingPage = lazyRoute(() => import('@/pages/admin/counselling/CounsellingPage'));
const AdminAffiliatesPage = lazyRoute(() => import('@/pages/admin/affiliates/AffiliatesPage'));
const AdminLivePage = lazyRoute(() => import('@/pages/admin/live/LivePage'));
const AdminMasterclassPage = lazyRoute(() => import('@/pages/admin/masterclass/MasterclassPage'));
const AdminCertsPage = lazyRoute(() => import('@/pages/admin/certs/CertsPage'));
const AdminAnnouncePage = lazyRoute(() => import('@/pages/admin/announce/AnnouncePage'));
const AdminSettingsPage = lazyRoute(() => import('@/pages/admin/settings/SettingsPage'));
const AdminAuditPage = lazyRoute(() => import('@/pages/admin/audit/AuditPage'));
const AdminJobsPage = lazyRoute(() => import('@/pages/admin/jobs/JobsPage'));
const PartnerOverviewPage = lazyRoute(() => import('@/pages/partner/overview/OverviewPage'));
const PartnerLinkPage = lazyRoute(() => import('@/pages/partner/afflink/AffiliateLinkPage'));
const PartnerCoursesPage = lazyRoute(() => import('@/pages/partner/affcourses/AffiliateCoursesPage'));
const PartnerSalesPage = lazyRoute(() => import('@/pages/partner/affsales/AffiliateSalesPage'));
const StaffProfilePage = lazyRoute(() => import('@/pages/staff/profile/StaffProfilePage'));
const OrderManagerOverviewPage = lazyRoute(() => import('@/pages/order-manager/overview/OverviewPage'));
const OrderManagerOrdersPage = lazyRoute(() => import('@/pages/order-manager/orders/OrdersPage'));
const OrderManagerBookOrdersPage = lazyRoute(() => import('@/pages/order-manager/book-orders/BookOrdersPage'));
const NotBuiltPage = lazyRoute(() => import('@/pages/public/system/NotBuilt'));

const publicRoutes: RouteObject[] = [
  // Home landed in Phase 9 — "/" is the dark home page, no longer a redirect.
  { index: true, lazy: HomePage },
  { path: '/courses', lazy: CatalogPage },
  { path: '/course/:slug', lazy: CourseDetailPage },
  { path: '/store', lazy: StorePage },
  { path: '/leaderboard', lazy: LeaderboardPage },
  { path: '/affiliate', lazy: AffiliatePage },
  { path: '/affiliate/register', Component: AffiliateRegisterPage },
  { path: '/centres', lazy: CentresPage },
  { path: '/guides', lazy: BlogPage },
  { path: '/guides/:slug', lazy: BlogPostPage },
  /** Legacy URLs — page label is “Free notes & guides”. */
  { path: '/blog', Component: GuidesIndexRedirect },
  { path: '/blog/:slug', Component: GuidesSlugRedirect },
  { path: '/resources', lazy: ResourcesPage },
  { path: '/instructors', lazy: InstructorsPage },
  { path: '/instructors/:slug', lazy: InstructorProfilePage },
  { path: '/category/:slug', lazy: CategoryPage },
  { path: '/event/:slug', lazy: EventPage },
  /**
   * Marketing / company pages — each has its own component instead of the old
   * /page/:slug CMS catch-all. React Router ranks static segments above dynamic
   * ones, so explicit paths win over any future param routes.
   */
  { path: '/page/about-us', lazy: AboutUsPage },
  { path: '/page/become-an-instructor', lazy: BecomeInstructorPage },
  { path: '/page/careers', lazy: CareersPage },
  { path: '/page/careers/:slug', lazy: JobDetailPage },
  { path: '/page/contact', lazy: ContactPage },
  { path: '/page/for-business', lazy: ForBusinessPage },
  { path: '/page/help-center', lazy: HelpCenterPage },
  { path: '/page/privacy-policy', lazy: PrivacyPolicyPage },
  { path: '/page/refund-policy', lazy: RefundPolicyPage },
  { path: '/page/terms-of-service', lazy: TermsOfServicePage },
  { path: '/cv-builder', lazy: CvBuilderPage },
  { path: '/verify', lazy: VerifyPage },
  { path: '/verify/:certId', lazy: VerifyPage },
  { path: '/login', Component: LoginPage },
  { path: '/register', Component: RegisterPage },
];

const protectedRoutes: RouteObject[] = [
  { path: '/learn/:slug/:module/:lesson', lazy: PlayerPage },
  {
    path: '/dashboard',
    lazy: StudentLayout,
    children: [
      { index: true, lazy: DashboardIndex },
      { path: 'overview', lazy: StudentOverviewPage },
      { path: 'purchased', lazy: StudentPurchasedPage },
      { path: 'free', lazy: StudentFreeCoursesPage },
      { path: 'completed', lazy: StudentCompletedPage },
      { path: 'certs', lazy: StudentCertificatesPage },
      { path: 'wishlist', lazy: StudentWishlistPage },
      { path: 'cart', lazy: StudentCartPage },
      { path: 'practice', lazy: StudentPracticePage },
      { path: 'assignments', lazy: StudentAssignmentsPage },
      { path: 'orders', lazy: StudentOrdersPage },
      { path: 'support', lazy: StudentSupportPage },
      { path: 'settings', lazy: StudentSettingsPage },
      { path: '*', lazy: DashboardIndex },
    ],
  },
];

const adminPanelChildren: RouteObject[] = [
  { index: true, lazy: AdminIndex },
  { path: 'overview', lazy: AdminOverviewPage },
  { path: 'reports', lazy: AdminReportsPage },
  { path: 'courses', lazy: AdminCoursesPage },
  { path: 'courses/new', lazy: AdminAddCoursePage },
  { path: 'bundles', lazy: AdminBundlesPage },
  { path: 'qbank', lazy: AdminQbankPage },
  { path: 'students', lazy: AdminStudentsPage },
  { path: 'order-managers', lazy: AdminOrderManagersPage },
  { path: 'orders', lazy: AdminOrdersPage },
  { path: 'books', lazy: AdminBooksPage },
  { path: 'book-orders', lazy: AdminBookOrdersPage },
  { path: 'payouts', lazy: AdminPayoutsPage },
  { path: 'affiliate-payouts', lazy: AdminAffiliatePayoutsPage },
  { path: 'coupons', lazy: AdminCouponsPage },
  { path: 'assignments', lazy: AdminAssignmentsPage },
  { path: 'assignments/new', lazy: AdminAddAssignmentPage },
  { path: 'assignments/:id/edit', lazy: AdminAddAssignmentPage },
  { path: 'content', lazy: AdminContentPage },
  { path: 'downloads', lazy: AdminDownloadsPage },
  { path: 'qa', lazy: AdminQaPage },
  { path: 'tickets', lazy: AdminTicketsPage },
  { path: 'reviews', lazy: AdminReviewsPage },
  { path: 'instructors', lazy: AdminInstructorsPage },
  { path: 'leads', lazy: AdminLeadsPage },
  { path: 'free-resources', lazy: AdminFreeResourcesPage },
  { path: 'counselling', lazy: AdminCounsellingPage },
  { path: 'affiliates', lazy: AdminAffiliatesPage },
  { path: 'live', lazy: AdminLivePage },
  { path: 'masterclass', lazy: AdminMasterclassPage },
  { path: 'certs', lazy: AdminCertsPage },
  { path: 'announce', lazy: AdminAnnouncePage },
  { path: 'settings', lazy: AdminSettingsPage },
  { path: 'audit', lazy: AdminAuditPage },
  { path: 'jobs', lazy: AdminJobsPage },
];

const instructorPanelChildren: RouteObject[] = [
  { index: true, lazy: AdminIndex },
  { path: 'overview', lazy: AdminOverviewPage },
  { path: 'profile', lazy: StaffProfilePage },
  { path: 'reports', lazy: AdminReportsPage },
  { path: 'courses', lazy: AdminCoursesPage },
  { path: 'courses/new', lazy: AdminAddCoursePage },
  { path: 'qbank', lazy: AdminQbankPage },
  { path: 'assignments', lazy: AdminAssignmentsPage },
  { path: 'assignments/new', lazy: AdminAddAssignmentPage },
  { path: 'assignments/:id/edit', lazy: AdminAddAssignmentPage },
  { path: 'reviews', lazy: AdminReviewsPage },
  { path: 'payment', lazy: AdminPaymentPage },
];

const partnerPanelChildren: RouteObject[] = [
  { index: true, lazy: AdminIndex },
  { path: 'overview', lazy: PartnerOverviewPage },
  { path: 'profile', lazy: StaffProfilePage },
  { path: 'afflink', lazy: PartnerLinkPage },
  { path: 'affcourses', lazy: PartnerCoursesPage },
  { path: 'affsales', lazy: PartnerSalesPage },
  { path: 'payment', lazy: AdminPaymentPage },
];

const orderManagerPanelChildren: RouteObject[] = [
  { index: true, lazy: AdminIndex },
  { path: 'overview', lazy: OrderManagerOverviewPage },
  { path: 'orders', lazy: OrderManagerOrdersPage },
  { path: 'book-orders', lazy: OrderManagerBookOrdersPage },
];

const adminRoutes: RouteObject[] = [
  {
    path: '/admin',
    lazy: AdminLayout,
    children: adminPanelChildren,
  },
  {
    path: '/instructor',
    lazy: AdminLayout,
    children: instructorPanelChildren,
  },
  {
    path: '/partner',
    lazy: AdminLayout,
    children: partnerPanelChildren,
  },
  {
    path: '/order-manager',
    lazy: AdminLayout,
    children: orderManagerPanelChildren,
  },
];

export const routes: RouteObject[] = [
  {
    element: <Layout />,
    children: [
      ...publicRoutes,
      ...protectedRoutes,
      ...adminRoutes,
      { path: '*', lazy: NotBuiltPage },
    ],
  },
];

export { ProtectedRoute } from '@/routes/ProtectedRoute';
